import { isPlatformBrowser } from '@angular/common';
import { FilterOptionsEnum } from './../../../../../shared/models/filter-options-enum';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Inject,
  inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { getDecryptedToken } from '../../../../../shared/utils/safe-storage';
import { syncBlobTokens } from '../../../../../shared/utils/blob-token-sync';
import { jwtDecode } from 'jwt-decode';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Category } from '../../../../admin-panel/components/pages/category/models/category';
import { AuthService } from '../../../../auth/services/auth.service';
import { CategoryService } from '../../../../admin-panel/components/pages/category/services/category.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { environment } from '../../../../../environments/environment';
import {DropdownToggleService} from "../../../../../shared/services/dropdown-toggle.service";
import { CacheService } from '../../../../../shared/services/cache.service';
import { ProfileRoutes } from '../../../../contributor/components/profile/profile.routes';
import { LocalStorageKeysEnum } from '../../../../../shared/models/localStorageKeys-enum';
import { StringUtils } from '../../../../../utils/guid';
import { SpecialCharactersFormatter } from '../../../../../utils/convert-special-formatter';
import { NavigationUtils } from '../../../../../utils/navigation-utils';
import { PackService } from '../../../../contributor/components/profile/contributors/pack/services/pack.service';
import { ProxyServiceFetchURLService } from '../../../../../shared/services/proxy-service-fetch-url.service';
import { LisenceEnum } from '../../../../../shared/models/license';
import { AppRoutesUtils } from '../../../../../utils/app-routes-util';
import { AssetGalleryParams } from '../../../../../shared/models/asset-gallery-params';
import { ParamsUtil } from '../../../../../utils/params-util';
import { filter, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PlaceholderEnum } from '../../../../../shared/models/placeholder';
import { BlobService } from '../../../../../shared/services/blob.service';
import { FormatterUtil } from '../../../../../utils/formatter-util';
import { HeaderSearchService } from '../../../services/header-search.service';
import { appendBlobSasToken } from '../../../../../shared/utils/blob-url-util';


@Component({
  selector: 'app-header',
  standalone:false,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  encodeURIComponent = encodeURIComponent;
  intervalId: any;
  showAuthOptions: boolean = false;
  userName: string | undefined;
  userPic: string | undefined;
  categories: Category[] = [];
  search: string = '';
  style:string=FilterOptionsEnum.None;
  assetCategory:string=FilterOptionsEnum.None;
  isLoggedIn = false;
  isDropdownOpen = false;
  receivedUserImage: string | undefined;
  placeholderImage: string = PlaceholderEnum.Image;
  key: string = 'categories';
  categorySelected: string = 'All Assets';
  isOverlay: boolean = false;
  hoveredCategory: string | null = 'Animations';
  /** Set on first hover/focus/touch of "Explore" so the menu preview (Lottie) is ready when it opens. */
  exploreMenuWarm = false;
  showHeaderSearch: boolean = false;
  private headerSearchForced = false;
  private destroy$ = new Subject<void>();

  lisenceEnum: any = LisenceEnum;
  content: string = LisenceEnum.All.toString().toLowerCase();
  token!: string | null;
  readonly appendBlobSasToken = appendBlobSasToken;
  readonly tools = [
    { name: 'AI Image Generation', description: 'Create images from text prompts', path: '/tool/image-generation' },
    { name: 'Image Upscaler', description: 'Enlarge images without losing quality', path: '/tool/image-upscaler' },
    { name: 'Image Colorize', description: 'Add color to black and white images', path: '/tool/image-colorize' },
    { name: 'Background Remover', description: 'Remove image backgrounds instantly', path: '/tool/background-remover' },
    { name: 'Image Plagiarism Checker', description: 'Check if an image is original', path: '/tool/image-plagiarism-checker' },
    { name: 'Caption Generator', description: 'Generate captions for your images', path: '/tool/caption-generator' },
    { name: 'Resize Image', description: 'Change image dimensions quickly', path: '/tool/resize-image' },
    { name: 'Merge Images', description: 'Combine multiple images into one', path: '/tool/merge-images' },
    { name: 'Merge Grid', description: 'Arrange images in a grid layout', path: '/tool/merge-grid' },
    { name: 'Merge PDF', description: 'Combine PDF files into a single file', path: '/tool/merge-pdf' },
    { name: 'SVG Editor', description: 'Edit and customize SVG files', path: '/tool/svg-editor' },
  ];

  warmExploreMenu(): void {
    this.exploreMenuWarm = true;
  }

  onUserImage(imageUrl: string): void {
    this.receivedUserImage =
      imageUrl && imageUrl.trim() !== '' ? imageUrl : this.placeholderImage;
  }

  /**
   * Final header avatar URL (set once after pic + SAS are ready).
   * `null` = still loading → pulse placeholder only.
   */
  headerAvatarSrc: string | null = null;

  private normalizeUserPic(pic: string | null | undefined): string | null {
    if (pic == null) {
      return null;
    }
    const trimmed = pic.trim();
    if (
      !trimmed ||
      trimmed === 'null' ||
      trimmed === 'undefined' ||
      trimmed.includes('avatars/blank.png')
    ) {
      return null;
    }
    return trimmed;
  }

  private buildHeaderAvatarSrc(): string {
    const pic = this.normalizeUserPic(this.userPic);
    if (!pic) {
      return this.placeholderImage;
    }
    return appendBlobSasToken(pic, this.blobService.getToken() ?? this.token);
  }

  /** Load user pic + blob token, then bind avatar once. */
  private async resolveHeaderAvatar(): Promise<void> {
    this.getUserInfo();
    if (isPlatformBrowser(this.platformId)) {
      try {
        await this.getblobToken();
      } catch {
        // Keep decrypted token if refresh fails.
      }
    }
    this.headerAvatarSrc = this.buildHeaderAvatarSrc();
    this.cdr.markForCheck();
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private sharedService: SharedService,
    protected dropdownService: DropdownToggleService,
    private cacheService: CacheService,
    private activatedRoute: ActivatedRoute,
    private packService: PackService,
    public proxyServiceFetchURLService: ProxyServiceFetchURLService,
    private blobService: BlobService,
    private HeaderSearchService: HeaderSearchService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
      this.showAuthOptions = this.authService.isUserLogin();
      syncBlobTokens({
        blobService: this.blobService,
        destroyRef: this.destroyRef,
        // Do not rewrite headerAvatarSrc here — that re-bound [userPic] and
        // caused loader → photo → loader → photo.
        setToken: (t) => (this.token = t),
        cdr: this.cdr,
      });
  }

  async ngOnInit() {
    this.token = getDecryptedToken();
    this.showAuthOptions = this.authService.isUserLogin();
    if (this.showAuthOptions) {
      this.headerAvatarSrc = null;
      void this.resolveHeaderAvatar();
    } else {
      this.headerAvatarSrc = null;
    }
    this.sharedService.currentSearch.subscribe((e) => {
      this.search = e;
      this.cdr.markForCheck();
    });
    this.sharedService.categorySearch.subscribe((e) => {
      this.categorySelected = e;
    });
    const content = this.sharedService.getContent();
    const contentparam = this.activatedRoute.snapshot.queryParamMap.get('content');
    if(contentparam!=content && contentparam!=null){
     this.sharedService.setContent(contentparam!);
    }
    this.content = this.sharedService.getContent();
    this.sharedService.styleFilter.subscribe((e) => {
      this.style = e;
    });
    this.sharedService.assetCategoryFilter.subscribe((e) => {
      this.assetCategory = e;
    });

    this.getCategoriesFromCache();

    this.authService.refreshComponent$.subscribe((response: boolean) => {
      if (response) {
        // Covers login and logout without a full page reload.
        this.syncAuthHeaderState();
      }
    });
    this.HeaderSearchService.showHeaderSearch$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        this.showHeaderSearch = value;
      });

    // Close the open asset/category dropdown on scroll. Scroll events do not
    // bubble, so on inner pages the page often scrolls inside a nested
    // container rather than the window — listen in the capture phase on the
    // document so it fires regardless of which element actually scrolls.
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('scroll', this.onScrollCapture, true);
    }
  }

  private scrollCloseTimer: any = null;

  private onScrollCapture = (): void => {
    // Only schedule a close while the dropdown is open and nothing is pending.
    if (!this.isDropdownVisible('headerSearch') || this.scrollCloseTimer) {
      return;
    }
    this.scrollCloseTimer = setTimeout(() => {
      this.scrollCloseTimer = null;
      if (this.isDropdownVisible('headerSearch')) {
        this.dropdownService.hideDropdown('headerSearch');
        this.cdr.markForCheck();
      }
    }, 1000);
  };

  // Cancel a pending scroll-triggered close so manual open/close stays instant.
  private clearScrollCloseTimer(): void {
    if (this.scrollCloseTimer) {
      clearTimeout(this.scrollCloseTimer);
      this.scrollCloseTimer = null;
    }
  }

  /** Auth UI is resolved in the browser only so SSR cookie state cannot flash a logged-in header for guests. */
  syncAuthHeaderState(): void {
    const loggedIn = this.authService.isUserLogin();
    this.showAuthOptions = loggedIn;
    if (loggedIn) {
      this.headerAvatarSrc = null;
      void this.resolveHeaderAvatar();
    } else {
      this.userName = undefined;
      this.userPic = undefined;
      this.headerAvatarSrc = null;
    }
    this.cdr.markForCheck();
  }
async getblobToken() {
    this.token = (await firstValueFrom(this.blobService.getBlobToken())) ?? '';
  }
  getUserInfo(): void {
    const auth = this.authService.getAuthFromLocalStorage();
    if (auth) {
      const decoded = jwtDecode(auth.authToken) as any;
      this.userName = decoded.name;
      this.userPic = this.normalizeUserPic(decoded.pic) ?? undefined;
    }
  }

   getCategoriesFromCache() {
    this.cacheService.getCategoriesFromCache().subscribe({
      next: (response: Category[]) => {
        this.categories = response;
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 10);
      },
    });
  }
  getSubCategories(item: Category): Category[] {
    return this.categories.filter((i) => i.parentID === item.categoryID);
  }
  isChildsExist(item: Category): boolean {
    return this.categories.some((i) => i.parentID === item.categoryID);
  }
  navigate(item: Category) {
    AppRoutesUtils.Pack.navigateToPackGallery(item);
  }

  getPackGalleryPath(item: Category | undefined): string[] | null {
    return AppRoutesUtils.Pack.getPackGalleryPath(item);
  }
onCategoryClick(item: Category | undefined): void {
  this.sharedService.setCategory(item?.name!);
}
  changeSearch(evt: Event): void {
    const inputElement = evt.target as HTMLInputElement;
    const trimmedValue = inputElement.value.trim();
    inputElement.value = trimmedValue;
    this.search =
      SpecialCharactersFormatter.replaceAndSanitizeSpecialCharacters(
        trimmedValue
      );
    if (this.search.length) {
      this.sharedService.changeSearch(this.search);
    }
    if (this.search.length && this.categorySelected) {
      this.navigateToAssetGallery();
    }
    this.dismissSearchUI(inputElement);
  }
  OnEnterSearch(): void {
    this.search =
      SpecialCharactersFormatter.replaceAndSanitizeSpecialCharacters(
        this.search.trim()
      );
    if (this.search.trim().length && this.categorySelected) {
      this.navigateToAssetGallery();
    }
    this.dismissSearchUI();
  }

  // After submitting a search: close any open dropdowns and let the scroll
  // listener re-evaluate the bar's visibility (so it can collapse on mobile
  // when at the top of the page).
  private dismissSearchUI(inputElement?: HTMLInputElement): void {
    inputElement?.blur();
    this.dropdownService.hideAllDropdowns();
    this.isOverlay = false;
    this.resetForcedHeaderSearch();
  }

  navigateToAssetGallery(): void {
    let params: AssetGalleryParams = {
      category: this.categorySelected,
      search: this.search
    };
    if(this.content==='all' || this.content==='None' || this.content===''){
      AppRoutesUtils.Asset.navigateToAssetGallery(params);
    }
    else{

      const queryParams: { [key: string]: any } = {
        content: this.content,
      };
      AppRoutesUtils.Asset.navigateToAssetGalleryViaParams(params, queryParams);
    }
  }
  handlePaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const items = clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          event.preventDefault();
          return;
        }
      }
    }
  }

  clearSearch(): void {
    this.search = StringUtils.EMPTY;
    NavigationUtils.removeQueryParamFromCurrentRoute(
      this.router,
      this.activatedRoute,
      'search'
    );
  }
  isLandingPageRoute(): boolean {
    return this.router.url.split('?')[0] === '/';
  }

  // Toggle a specific dropdown
  onMenuKeydown(event: KeyboardEvent, dropdownId: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown(dropdownId, event);
    }
  }

  toggleDropdown(id: string, event: Event): void {
    event.stopPropagation(); // Prevent the document click listener from closing the dropdown
    this.clearScrollCloseTimer(); // user-driven open/close should behave normally
    this.dropdownService.toggleDropdown(id);
    this.isOverlay = !this.isOverlay;
    // On mobile/tablet the avatar/explore dropdowns are positioned `fixed
    // top-37.5`, which assumes the search bar row is part of the header. If
    // the search bar is currently hidden (e.g. at the top of the landing
    // page) the dropdown appears detached — force the search bar visible so
    // they line up.
    const isAnchored =
      id === 'userOptionToggle' || id === 'exploreMenu' || id === 'toolsMenu';
    const isNowOpen = this.isDropdownVisible(id);
    if (isAnchored && this.isSmallScreen() && isNowOpen) {
      this.HeaderSearchService.lockVisible();
      this.headerSearchForced = true;
    } else if (isAnchored && !isNowOpen && this.headerSearchForced) {
      // Re-tapping the same dropdown closes it but stops propagation, so
      // `onDocumentClick` never fires — release the lock here too, otherwise
      // the search bar would stay latched visible.
      this.resetForcedHeaderSearch();
    }
  }

  // Check if a dropdown is visible
  isDropdownVisible(id: string): boolean {
    return this.dropdownService.isDropdownVisible(id);
  }

  onLinkClick(event: Event): void {
    event.stopPropagation(); // Prevent interference with dropdown toggling
    this.dropdownService.hideAllDropdowns();
    this.isOverlay = false;
    this.resetForcedHeaderSearch();
  }

  // Keep clicks inside the search bar from reaching `onDocumentClick`, which
  // would otherwise close the category dropdown, release the visibility lock,
  // and re-fire scroll logic that can hide the bar while the user is typing.
  onSearchBarClick(event: Event): void {
    event.stopPropagation();
  }

  // Hide all dropdowns when clicking anywhere else in the document
  @HostListener('document:click')
  onDocumentClick(): void {
    this.dropdownService.hideAllDropdowns();
    this.isOverlay = false;
    this.resetForcedHeaderSearch();
  }

  private isSmallScreen(): boolean {
    return isPlatformBrowser(this.platformId) && window.innerWidth < 1024;
  }

  private resetForcedHeaderSearch(): void {
    if (!this.headerSearchForced || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.headerSearchForced = false;
    this.HeaderSearchService.unlock();
    // Let the landing page's scroll listener re-evaluate visibility — hide at
    // top, keep shown when scrolled past the hero.
    window.dispatchEvent(new Event('scroll'));
  }

  onSelectCategory(category: string): void {
    this.categorySelected = category;
    this.sharedService.setCategory(this.categorySelected);
    this.sharedService.setRefreshIconGallery(true);
    this.dropdownService.hideDropdown('headerSearch');
    if (this.search.length && this.categorySelected) {
      this.navigateToAssetGallery();
    }
  }

  /** Hover / crawlable URL for search-bar category items (not All Assets). */
  getCategoryDropdownHref(name: string): string {
    const category = (name || '').trim();
    if (!category) {
      return '/';
    }
    if ((this.search || '').trim()) {
      return AppRoutesUtils.Asset.getAssetGalleryViaUrl({
        category,
        search: this.search,
      });
    }
    return '/' + FormatterUtil.formatToKebabCase(category);
  }

  onCategoryDropdownClick(event: MouseEvent, category: string): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey) {
      return;
    }
    event.preventDefault();
    this.onSelectCategory(category);
  }
onSelectLicenseType(content: string): void {
  if (this.content === content) {
    this.content = '';
    this.sharedService.setContent(this.content);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { content: null },
      queryParamsHandling: 'merge'
    });
  } else {
    this.content = content;
    if (content !== 'all') {
      this.sharedService.setContent(this.content);
    }
  }

  this.sharedService.setRefreshIconGallery(true);
  this.dropdownService.hideDropdown('headerSearch');

  if (this.search.length && this.categorySelected && this.content) {
    this.navigateToAssetGallery();
  }
}
  ngOnDestroy(): void {
    this.sharedService.setSearch('');
    this.clearScrollCloseTimer();
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('scroll', this.onScrollCapture, true);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
